import React, { useEffect } from "react";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  FormHelperText
} from "@material-ui/core";
import { Form, Field } from "react-final-form";
import { IntroText, FormActionDrawer } from "../../../components";
import createDecorator from "final-form-focus";
import { useHeader } from "../../../contexts";
import { string, object } from "yup";
import { useYupValidation } from "../../../hooks";
import {
  AVAILITY_SUBSCRIBER_RELATIONSHIPS,
  AVAILITY_SUBSCRIBER_RELATIONSHIP_SELF
} from "../../../constants";
import { useTranslation } from "react-i18next";

const focusOnErrors = createDecorator();

const validationSchema = object({
  payer: string()
    .required()
    .trim()
    .label("Insurance company name"),
  member_id: string()
    .required()
    .trim()
    .label("Member ID"),
  group_no: string()
    .required()
    .trim()
    .label("Group Number"),
  relationship: string()
    .required()
    .trim()
    .label("Relationship"),
  level: string()
    .required()
    .trim()
    .label("Insurance level")
});

const ScheduleInsuranceVerification = ({
  setDrawerClearance,
  updateScheduleData,
  scheduleData,
  isSubmitting
}) => {
  const { t } = useTranslation();
  const { setTitle } = useHeader();
  const { validate } = useYupValidation();

  useEffect(() => {
    setTitle(t("schedule.insurance_verification.title"));
  }, []);

  const validateForm = async data => {
    const errors = await validate(validationSchema)(data);

    if (errors) return errors;

    updateScheduleData({
      insuranceVerification: data
    });
  };

  const getInitialValues = () => {
    let initialValues = {};

    // Set relationship
    if (scheduleData.isPatient) {
      const [relationship] = AVAILITY_SUBSCRIBER_RELATIONSHIPS.filter(
        relationship =>
          relationship.code === AVAILITY_SUBSCRIBER_RELATIONSHIP_SELF
      );

      if (relationship) {
        initialValues = { ...initialValues, relationship: relationship.code };
      }
    }

    return initialValues;
  };

  return (
    <>
      <Form
        onSubmit={validateForm}
        initialValues={{
          ...getInitialValues(),
          ...scheduleData.insuranceVerification
        }}
        decorators={[focusOnErrors]}
        render={({ handleSubmit, submitErrors: errors = {} }) => (
          <form
            data-cy="schedule-screen-insurance-verification"
            onSubmit={handleSubmit}
          >
            <IntroText>
              {t("schedule.insurance_verification.intro_text")}
            </IntroText>
            <Field
              name="payer"
              render={({ input }) => (
                <TextField
                  {...input}
                  label={t("schedule.insurance_verification.form.payer.label")}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  error={!!errors.payer}
                  helperText={errors.payer}
                  inputProps={{
                    "data-lpignore": "true",
                    "data-cy": "input-payer"
                  }}
                />
              )}
            />
            <Field
              name="member_id"
              render={({ input }) => (
                <TextField
                  {...input}
                  label={t(
                    "schedule.insurance_verification.form.member_id.label"
                  )}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  error={!!errors.member_id}
                  helperText={errors.member_id}
                  inputProps={{
                    "data-lpignore": "true",
                    "data-cy": "input-member_id"
                  }}
                />
              )}
            />
            <Field
              name="group_no"
              render={({ input }) => (
                <TextField
                  {...input}
                  label={t(
                    "schedule.insurance_verification.form.group_no.label"
                  )}
                  fullWidth
                  margin="normal"
                  variant="outlined"
                  error={!!errors.group_no}
                  helperText={errors.group_no}
                  inputProps={{
                    "data-lpignore": "true",
                    "data-cy": "input-group_no"
                  }}
                />
              )}
            />
            {!scheduleData.isPatient && (
              <Field
                name="relationship"
                render={({ input }) => (
                  <FormControl margin="normal" fullWidth variant="outlined">
                    <InputLabel id="insurance-verification-relationship">
                      {t(
                        "schedule.insurance_verification.form.relationship.label"
                      )}
                    </InputLabel>
                    <Select
                      {...input}
                      native
                      labelId="insurance-verification-relationship"
                      id="insurance-verification-relationship"
                      label={t(
                        "schedule.insurance_verification.form.relationship.label"
                      )}
                      fullWidth
                      error={!!errors.relationship}
                      inputProps={{ "data-cy": "input-relationship" }}
                    >
                      {[
                        { code: "", label: "" },
                        ...AVAILITY_SUBSCRIBER_RELATIONSHIPS
                      ]
                        .filter(
                          relationship =>
                            relationship.code !==
                            AVAILITY_SUBSCRIBER_RELATIONSHIP_SELF
                        )
                        .map(relationship => (
                          <option
                            key={`relationship-${relationship.code}`}
                            value={relationship.code}
                          >
                            {relationship.label}
                          </option>
                        ))}
                    </Select>
                    <FormHelperText>{errors.relationship}</FormHelperText>
                  </FormControl>
                )}
              />
            )}
            <Field
              name="level"
              render={({ input }) => (
                <FormControl margin="normal" fullWidth variant="outlined">
                  <InputLabel id="insurance-verification-level">
                    Insurance Level
                  </InputLabel>
                  <Select
                    {...input}
                    native
                    labelId="insurance-verification-level"
                    id="insurance-verification-level"
                    label="Insurance Level"
                    fullWidth
                    inputProps={{ "data-cy": "input-level" }}
                  >
                    <option value=""></option>
                    <option value="primary">Primary</option>
                    <option value="secondary">Secondary</option>
                    <option value="secondary">tertiary</option>
                  </Select>
                  {!!errors.level && (
                    <FormHelperText error={true}>{errors.level}</FormHelperText>
                  )}
                </FormControl>
              )}
            />
            <FormActionDrawer
              setDrawerClearance={setDrawerClearance}
              loading={isSubmitting}
            />
          </form>
        )}
      />
    </>
  );
};

export { ScheduleInsuranceVerification };
